/**
 * Knowledge Base API Client - HTTP client for project knowledge base endpoints
 */

import type {
  CreateKnowledgeEntryRequest,
  UpdateKnowledgeEntryRequest,
  KnowledgeEntryResponse,
  KnowledgeUploadUrlRequest,
  KnowledgeUploadUrlResponse,
  CreateKnowledgeFileEntryRequest,
  KnowledgeDownloadUrlResponse,
  ReplaceKnowledgeAttachmentRequest,
} from '@resolve-ai/shared';

import { fetchWithAuth } from './api-client';

export interface KnowledgeImportSummary {
  created: number;
  updated: number;
  unchanged: number;
  skipped: number;
}

export const knowledgeApi = {
  /**
   * List all knowledge entries for a project
   */
  async list(projectId: string): Promise<KnowledgeEntryResponse[]> {
    const response = await fetchWithAuth<{ entries: KnowledgeEntryResponse[] }>(
      `/projects/${projectId}/knowledge`
    );
    return response.entries;
  },

  /**
   * Create a knowledge entry
   */
  async create(
    projectId: string,
    data: CreateKnowledgeEntryRequest
  ): Promise<KnowledgeEntryResponse> {
    return fetchWithAuth<KnowledgeEntryResponse>(`/projects/${projectId}/knowledge`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  /**
   * Update a knowledge entry
   */
  async update(
    projectId: string,
    entryId: string,
    data: UpdateKnowledgeEntryRequest
  ): Promise<KnowledgeEntryResponse> {
    return fetchWithAuth<KnowledgeEntryResponse>(`/projects/${projectId}/knowledge/${entryId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  /**
   * Delete a knowledge entry
   */
  async delete(projectId: string, entryId: string): Promise<void> {
    await fetchWithAuth<void>(`/projects/${projectId}/knowledge/${entryId}`, {
      method: 'DELETE',
    });
  },

  /**
   * Re-import guidance files from every GitHub repo configured for the project
   */
  async importFromRepos(projectId: string): Promise<KnowledgeImportSummary> {
    return fetchWithAuth<KnowledgeImportSummary>(`/projects/${projectId}/knowledge/import`, {
      method: 'POST',
    });
  },

  /**
   * Mint a presigned S3 upload URL for a file attachment
   */
  async getUploadUrl(
    projectId: string,
    data: KnowledgeUploadUrlRequest
  ): Promise<KnowledgeUploadUrlResponse> {
    return fetchWithAuth<KnowledgeUploadUrlResponse>(`/projects/${projectId}/knowledge/upload-url`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  /**
   * PUT a file directly to S3 using a presigned URL. Deliberately bypasses
   * fetchWithAuth - S3 doesn't want this app's bearer token. contentType is
   * the one the URL was requested with: file.type is '' for .yaml, .log and
   * similar files, and S3 would then store the object with no type.
   */
  async uploadToS3(uploadUrl: string, file: File, contentType: string): Promise<void> {
    const response = await fetch(uploadUrl, {
      method: 'PUT',
      headers: { 'Content-Type': contentType },
      body: file,
    });

    if (!response.ok) {
      throw new Error(`Failed to upload file to storage (status ${response.status})`);
    }
  },

  /**
   * Register a completed direct-to-S3 upload as a knowledge entry
   */
  async createFileEntry(
    projectId: string,
    data: CreateKnowledgeFileEntryRequest
  ): Promise<KnowledgeEntryResponse> {
    return fetchWithAuth<KnowledgeEntryResponse>(`/projects/${projectId}/knowledge/file-entry`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  /**
   * Get a presigned URL to view/download a knowledge entry's attached file
   */
  async getDownloadUrl(projectId: string, entryId: string): Promise<string> {
    const response = await fetchWithAuth<KnowledgeDownloadUrlResponse>(
      `/projects/${projectId}/knowledge/${entryId}/download-url`
    );
    return response.url;
  },

  /**
   * Mint a presigned S3 upload URL to replace an existing entry's attachment
   */
  async getReplaceUploadUrl(
    projectId: string,
    entryId: string,
    data: KnowledgeUploadUrlRequest
  ): Promise<KnowledgeUploadUrlResponse> {
    return fetchWithAuth<KnowledgeUploadUrlResponse>(
      `/projects/${projectId}/knowledge/${entryId}/attachment-upload-url`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    );
  },

  /**
   * Finalize a replaced attachment upload
   */
  async replaceAttachment(
    projectId: string,
    entryId: string,
    data: ReplaceKnowledgeAttachmentRequest
  ): Promise<KnowledgeEntryResponse> {
    return fetchWithAuth<KnowledgeEntryResponse>(`/projects/${projectId}/knowledge/${entryId}/attachment`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },
};
